import { Loader2, Lock, LockOpen } from 'lucide-react'
import { cn } from '@/lib/utils/helpers'
import { ToggleSwitch } from './ToggleSwitch'

/**
 * "Reimbursement Lock" control — icon badge + toggle + status pill.
 * Shared by both the resident and reimbursement detail headers so the two
 * modules never drift out of sync visually.
 */
export function ClaimLockToggle({
  isLocked,
  statusLabel,
  canToggle,
  onToggle,
  isLoading = false,
  label = 'Reimbursement Lock',
  containerTestId,
  toggleTestId,
}: {
  isLocked: boolean
  /** Text shown in the pill — usually the raw `claim_lock` value ("Locked"/"Unlocked"). */
  statusLabel: string
  canToggle: boolean
  onToggle: () => void
  isLoading?: boolean
  label?: string
  containerTestId?: string
  toggleTestId?: string
}) {
  return (
    <div
      className={cn(
        'flex shrink-0 items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3 py-2 transition-opacity',
        isLoading && 'opacity-75',
      )}
      data-testid={containerTestId}
    >
      <span
        className={cn(
          'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg',
          isLocked ? 'bg-[#eaf4ff] text-[#1597ff]' : 'bg-slate-100 text-slate-500',
        )}
        aria-hidden="true"
      >
        {isLoading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-[#1597ff]" aria-hidden="true" />
        ) : isLocked ? (
          <Lock className="h-3.5 w-3.5" />
        ) : (
          <LockOpen className="h-3.5 w-3.5" />
        )}
      </span>
      <span className="whitespace-nowrap text-sm font-semibold text-slate-700">{label}</span>
      <ToggleSwitch
        checked={isLocked}
        onChange={onToggle}
        disabled={!canToggle || isLoading}
        offColorClass="bg-slate-300"
        thumbIcon={(checked) =>
          isLoading ? (
            <Loader2 className="h-2.5 w-2.5 animate-spin text-[#1597ff]" aria-hidden="true" />
          ) : checked ? (
            <Lock className="h-2.5 w-2.5 text-[#1597ff]" aria-hidden="true" />
          ) : (
            <LockOpen className="h-2.5 w-2.5 text-slate-400" aria-hidden="true" />
          )
        }
        aria-label={`Toggle ${label.toLowerCase()}`}
        data-testid={toggleTestId}
      />
      <span
        className={cn(
          'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide',
          isLocked ? 'bg-[#eaf4ff] text-[#1597ff]' : 'bg-slate-100 text-slate-600',
        )}
      >
        {isLoading ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" /> : null}
        {statusLabel}
      </span>
    </div>
  )
}
